var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/* eslint-disable react/function-component-definition */
import { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import OutlinedInput from '@mui/material/OutlinedInput';
import BackIcon from '@mui/icons-material/ArrowBack';
import QrCodeIcon from '@mui/icons-material/QrCodeScanner';
import { useAppSelector, useQuery } from '../../hooks';
import { useErrorHandler } from '../../hooks/useErrorHandler';
import { selectEvent } from '../../store/matches';
import { loadEvents } from '../../api/events';
import { loadPairedScreens, revokeScreen, suggestScreenEvent } from '../../api/screenPairing';
import { formatCode, formatEventLabel, formatTime, normalizeCode, SCREEN_CODE_LENGTH } from './utils';
import { codeInputSx, primaryButtonSx } from './styles';
import { isCameraAvailable, ScreenQrScanner } from './ScreenQrScanner';
import { usePairScreen } from './usePairScreen';
import { Alerts } from '../../components/molecules/Alerts/Alerts';
const SCREENS_REFRESH_MS = 30000;
const Page = styled.div `
  position: relative;
  box-sizing: border-box;
  width: 100%;
  min-height: 100vh;
  padding: 28px 36px;
  background: #292c2e;
  color: #ffffff;
  font-family: 'Roboto', sans-serif;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-wrap: anywhere;

  @media (max-width: 600px) {
    padding: 16px;
    gap: 16px;
  }
`;
const Header = styled.div `
  display: flex;
  align-items: center;
  gap: 16px;
  min-width: 0;
`;
const HeaderText = styled.div `
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;
const Title = styled.div `
  font-size: 24px;
  font-weight: 600;
`;
const Subtitle = styled.div `
  font-size: 14px;
  color: #899295;
`;
const Layout = styled.div `
  display: grid;
  grid-template-columns: minmax(0, 620px) minmax(0, 1fr);
  gap: 28px;
  align-items: start;
  min-width: 0;

  @media (max-width: 960px) {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
`;
const Column = styled.div `
  display: flex;
  flex-direction: column;
  gap: 20px;
  min-width: 0;

  @media (max-width: 600px) {
    gap: 16px;
  }
`;
const Hint = styled.div `
  min-height: 320px;
  box-sizing: border-box;
  border-radius: 12px;
  background: #161719;
  padding: 32px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  text-align: center;

  @media (max-width: 960px) {
    min-height: 0;
    padding: 20px;
  }
`;
const HintText = styled.div `
  max-width: 380px;
  font-size: 16px;
  line-height: 1.5;
  color: #c4cdd0;
`;
const Card = styled.form `
  box-sizing: border-box;
  min-width: 0;
  border-radius: 12px;
  background: #202224;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;

  @media (max-width: 600px) {
    padding: 16px;
  }
`;
const CardTitle = styled.label `
  font-size: 16px;
  font-weight: 500;
`;
const CardText = styled.div `
  font-size: 14px;
  line-height: 1.4;
  color: #899295;
`;
const EventLine = styled.div `
  font-size: 16px;
  line-height: 1.4;
  color: #c4cdd0;
`;
const EventName = styled.span `
  color: #ffffff;
  font-weight: 500;
`;
const ErrorText = styled.div `
  font-size: 14px;
  line-height: 1.4;
  color: #cc8181;
`;
const SuccessText = styled.div `
  font-size: 14px;
  line-height: 1.4;
  color: #81c784;
`;
const ScreenRow = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-top: 1px solid #3b3f42;
`;
const ScreenInfo = styled.div `
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
`;
const ScreenName = styled.div `
  font-size: 16px;
`;
const backButtonSx = {
    minWidth: 48,
    width: 48,
    height: 48,
    flexShrink: 0,
    background: '#3B3F42',
    color: '#FFFFFF',
    '&:hover': { background: '#4A4F53' },
};
const revokeButtonSx = {
    height: 44,
    flexShrink: 0,
    padding: '0 16px',
    background: 'transparent',
    boxShadow: 'none',
    border: '1px solid #CC8181',
    color: '#CC8181',
    '&:hover': { background: 'rgba(204, 129, 129, 0.12)', boxShadow: 'none' },
};
export const ScreenPair = () => {
    const nav = useNavigate();
    const { param: codeParam } = useQuery('code');
    const { param: eventIdParam } = useQuery('eventId');
    const storeEvent = useAppSelector(selectEvent);
    const { errorHandler } = useErrorHandler();
    const pair = usePairScreen();
    const [code, setCode] = useState(normalizeCode(codeParam));
    const [event, setEvent] = useState(null);
    const [eventState, setEventState] = useState('loading');
    const [isPairing, setPairing] = useState(false);
    // With a code already in the URL (native camera path) the camera is started only on demand
    const [isScannerOn, setScannerOn] = useState(!codeParam || !isCameraAvailable());
    const [pairError, setPairError] = useState(null);
    const [pairedCode, setPairedCode] = useState(null);
    const [screens, setScreens] = useState([]);
    const [revokingId, setRevokingId] = useState(null);
    // From the matches page the event is in the URL; from the TV QR the API suggests it
    useEffect(() => {
        const resolveEventName = (id) => __awaiter(void 0, void 0, void 0, function* () {
            if ((storeEvent === null || storeEvent === void 0 ? void 0 : storeEvent.id) === id) {
                setEvent({ id, name: storeEvent.name, date: storeEvent.date });
                return;
            }
            try {
                const events = yield loadEvents();
                const found = (events || []).find((e) => e.id === id);
                if (found)
                    setEvent({ id, name: found.name, date: found.date });
            }
            catch (e) {
                // the name is optional here: the pair response returns it
            }
        });
        const suggestEvent = () => __awaiter(void 0, void 0, void 0, function* () {
            try {
                const { event: suggested } = yield suggestScreenEvent();
                setEvent(suggested);
                setEventState(suggested ? 'resolved' : 'none');
            }
            catch (e) {
                errorHandler(e);
                setEventState('none');
            }
        });
        if (eventIdParam) {
            setEvent({ id: eventIdParam });
            setEventState('resolved');
            resolveEventName(eventIdParam);
        }
        else {
            suggestEvent();
        }
    }, [eventIdParam]);
    const eventId = (event === null || event === void 0 ? void 0 : event.id) || null;
    const getScreens = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!eventId) {
            setScreens([]);
            return;
        }
        try {
            setScreens(yield loadPairedScreens(eventId));
        }
        catch (e) {
            errorHandler(e);
        }
    }), [eventId]);
    useEffect(() => {
        getScreens();
        const timer = setInterval(getScreens, SCREENS_REFRESH_MS);
        return () => clearInterval(timer);
    }, [getScreens]);
    const isCodeComplete = code.length === SCREEN_CODE_LENGTH;
    const canPair = isCodeComplete && eventState === 'resolved' && !isPairing;
    const onCodeChange = (e) => {
        setCode(normalizeCode(e.target.value));
        setPairError(null);
        setPairedCode(null);
    };
    const pairWith = (pairCode) => __awaiter(void 0, void 0, void 0, function* () {
        setPairing(true);
        setPairError(null);
        setPairedCode(null);
        const { screen, error } = yield pair(pairCode, eventId);
        setPairing(false);
        if (!screen) {
            setPairError(error);
            return;
        }
        if (!(event === null || event === void 0 ? void 0 : event.name) || event.id !== screen.eventId)
            setEvent({ id: screen.eventId, name: screen.eventName });
        setPairedCode(pairCode);
        setCode('');
        getScreens();
    });
    const onSubmit = (e) => {
        e.preventDefault();
        if (canPair)
            pairWith(code);
    };
    const onDetected = (detected) => {
        setScannerOn(false);
        setCode(detected);
        if (eventState === 'resolved' && !isPairing)
            pairWith(detected);
    };
    const onRevoke = (screen) => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setRevokingId(screen.id);
            yield revokeScreen(screen.id);
            setScreens((current) => current.filter((s) => s.id !== screen.id));
        }
        catch (e) {
            errorHandler(e);
        }
        finally {
            setRevokingId(null);
        }
    });
    const onClickBack = () => nav(eventId ? `/matches?eventId=${eventId}` : '/');
    const eventLabel = event && (event.name || event.date) ? formatEventLabel(event) : null;
    return (_jsxs(Page, { children: [_jsx(Alerts, {}), _jsxs(Header, { children: [_jsx(Button, Object.assign({ "aria-label": "\u041D\u0430\u0437\u0430\u0434", sx: backButtonSx, onClick: onClickBack }, { children: _jsx(BackIcon, {}) })), _jsxs(HeaderText, { children: [_jsx(Title, { children: "\u041F\u043E\u0434\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u044D\u043A\u0440\u0430\u043D" }), eventLabel && _jsx(Subtitle, { children: eventLabel })] })] }), _jsxs(Layout, { children: [isScannerOn ? (_jsx(ScreenQrScanner, { onDetected: onDetected })) : (_jsxs(Hint, { children: [_jsx(QrCodeIcon, { sx: { fontSize: 96, color: '#FFFFFF', opacity: 0.9 } }), _jsx(HintText, { children: "\u041D\u0430\u0432\u0435\u0434\u0438\u0442\u0435 \u043A\u0430\u043C\u0435\u0440\u0443 \u043D\u0430 QR-\u043A\u043E\u0434 \u043D\u0430 \u044D\u043A\u0440\u0430\u043D\u0435 \u0438\u043B\u0438 \u0432\u0432\u0435\u0434\u0438\u0442\u0435 \u043A\u043E\u0434 \u0432\u0440\u0443\u0447\u043D\u0443\u044E" }), _jsx(Button, Object.assign({ sx: primaryButtonSx, onClick: () => setScannerOn(true) }, { children: "\u0421\u043A\u0430\u043D\u0438\u0440\u043E\u0432\u0430\u0442\u044C QR-\u043A\u043E\u0434" }))] })), _jsxs(Column, { children: [_jsxs(Card, Object.assign({ onSubmit: onSubmit }, { children: [_jsx(CardTitle, Object.assign({ htmlFor: "screen-code" }, { children: "\u041A\u043E\u0434 \u044D\u043A\u0440\u0430\u043D\u0430" })), _jsx(CardText, { children: "\u0412\u0432\u0435\u0434\u0438\u0442\u0435 \u043A\u043E\u0434, \u043A\u043E\u0442\u043E\u0440\u044B\u0439 \u043D\u0430\u043F\u0438\u0441\u0430\u043D \u043F\u043E\u0434 QR \u043D\u0430 \u044D\u043A\u0440\u0430\u043D\u0435" }), _jsx(FormControl, { children: _jsx(OutlinedInput, { id: "screen-code", placeholder: "K7Q-42M", value: formatCode(code), onChange: onCodeChange, autoComplete: "off", inputProps: { autoCapitalize: 'characters', spellCheck: false, maxLength: SCREEN_CODE_LENGTH + 1 }, sx: codeInputSx }) }), eventState === 'resolved' && eventLabel && (_jsxs(EventLine, { children: ['Афиша: ', _jsx(EventName, { children: eventLabel })] })), eventState === 'none' && (_jsxs(_Fragment, { children: [_jsx(EventLine, { children: "\u041E\u0442\u043A\u0440\u043E\u0439\u0442\u0435 \u043D\u0443\u0436\u043D\u0443\u044E \u0430\u0444\u0438\u0448\u0443 \u0438 \u043D\u0430\u0436\u043C\u0438\u0442\u0435 \u00AB\u041F\u043E\u0434\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u044D\u043A\u0440\u0430\u043D\u00BB" }), _jsx(Button, Object.assign({ variant: "contained", onClick: () => nav('/') }, { children: "\u041A \u0441\u043F\u0438\u0441\u043A\u0443 \u0430\u0444\u0438\u0448" }))] })), pairError && _jsx(ErrorText, Object.assign({ role: "alert" }, { children: pairError })), pairedCode && (_jsx(SuccessText, Object.assign({ role: "status" }, { children: `Экран ${formatCode(pairedCode)} подключён и покажет игру, которую вы ведёте` }))), _jsx(Button, Object.assign({ type: "submit", sx: primaryButtonSx, disabled: !canPair }, { children: "\u041F\u043E\u0434\u043A\u043B\u044E\u0447\u0438\u0442\u044C \u044D\u043A\u0440\u0430\u043D" })), _jsx(CardText, { children: "\u0420\u043E\u043B\u0438 \u0438 \u043D\u043E\u0447\u043D\u044B\u0435 \u0434\u0435\u0439\u0441\u0442\u0432\u0438\u044F \u043D\u0430 \u044D\u043A\u0440\u0430\u043D \u043D\u0435 \u043F\u0435\u0440\u0435\u0434\u0430\u044E\u0442\u0441\u044F." })] })), eventId && (_jsxs(Card, Object.assign({ as: "div" }, { children: [_jsx(CardTitle, Object.assign({ as: "div" }, { children: "\u041F\u043E\u0434\u043A\u043B\u044E\u0447\u0451\u043D\u043D\u044B\u0435 \u044D\u043A\u0440\u0430\u043D\u044B" })), screens.length === 0 && _jsx(CardText, { children: "\u0414\u043B\u044F \u044D\u0442\u043E\u0439 \u0430\u0444\u0438\u0448\u0438 \u044D\u043A\u0440\u0430\u043D\u043E\u0432 \u043F\u043E\u043A\u0430 \u043D\u0435\u0442" }), screens.map((screen) => (_jsxs(ScreenRow, { children: [_jsxs(ScreenInfo, { children: [_jsx(ScreenName, { children: `Экран ${formatCode(screen.code)}` }), _jsxs(CardText, { children: [`подключён в ${formatTime(screen.pairedAt)}`, screen.lastSeenAt
                                                                ? ` · на связи в ${formatTime(screen.lastSeenAt)}`
                                                                : ' · ещё не выходил на связь'] })] }), _jsx(Button, Object.assign({ sx: revokeButtonSx, disabled: revokingId === screen.id, onClick: () => onRevoke(screen) }, { children: "\u041E\u0442\u043A\u043B\u044E\u0447\u0438\u0442\u044C" }))] }, screen.id)))] })))] })] })] }));
};
